import { useId } from 'react';
import { Lightbulb, AlertTriangle } from 'lucide-react';
import type { HrsnDomain } from '@/types/api';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';

import { useLanguageStore } from '@/stores/language.store';
import {
  DOMAIN_SPECS,
  HOUSING_CONDITION_OPTIONS,
  FOOD_FREQUENCY_OPTIONS,
  SAFETY_LEVEL_OPTIONS,
  PHYSICAL_ABILITY_OPTIONS,
  computeSafetyTotal,
  type DomainAnswer,
} from './domains';
import {
  NOTES_MAX_LENGTH,
  notesPlaceholderFor,
  shouldShowNotesHint,
} from './notesPlaceholders';

interface DomainFormProps {
  domain: HrsnDomain;
  answer: DomainAnswer;
  onChange: (next: DomainAnswer) => void;
  notes: string;
  onNotesChange: (notes: string) => void;
}

export function DomainForm(props: DomainFormProps): JSX.Element {
  const { domain } = props;
  const spec = DOMAIN_SPECS[domain];
  const headingId = useId();
  const lang = useLanguageStore((s) => s.lang);
  const tr = (en: string, es: string): string => (lang === 'es' ? es : en);
  const title = lang === 'es' ? spec.titleEs : spec.title;
  const prompt = lang === 'es' ? spec.promptEs : spec.prompt;
  // F5-T7 (contrato Agente-03): la traducción al otro idioma va DEBAJO del
  // guion, en cursiva gris — quien lee en voz alta no puede dudar qué le toca.
  const promptAlt = lang === 'es' ? spec.prompt : spec.promptEs;
  const hooks = (lang === 'es' ? spec.hooksEs : spec.hooks) ?? [];
  const readAloud = lang === 'es' ? 'Lea en voz alta' : 'Read aloud';
  const hooksLabel = lang === 'es' ? 'Pistas · no leer' : "Hints · don't read";
  // S1 — regla del programa, no guion. Se pinta aparte de las pistas para que
  // nadie la lea en voz alta creyendo que es un follow-up.
  const note = lang === 'es' ? spec.noteEs : spec.note;

  return (
    <div className="space-y-6">
      {/* F5-T7 — jerarquía tipográfica de la llamada (contrato Agente-03):
          tres niveles SIN depender del color — rótulo versalitas teal, guion
          20px/600, traducción 14px cursiva gris, pistas en caja gris aparte.
          El guion NUNCA se abrevia ni se trunca. */}
      <header>
        <h2 id={headingId} className="sr-only">
          {title}
        </h2>
        <p className="font-head font-bold text-[10.5px] tracking-[.14em] uppercase text-nav-teal m-0 mb-2.5">
          {readAloud}
        </p>
        <p
          aria-live="polite"
          className="font-body text-[20px] font-semibold leading-[1.45] tracking-[-0.01em] text-nav-navy m-0"
        >
          {prompt}
        </p>
        {promptAlt !== prompt && (
          <p aria-live="polite" className="font-body text-sm italic leading-normal text-nav-slate mt-2 m-0">
            {promptAlt}
          </p>
        )}
        {hooks.length > 0 && (
          <div className="mt-4 bg-nav-softer border-[1.5px] border-nav-card rounded-xl px-[18px] py-4">
            <p className="font-head font-bold text-[10.5px] tracking-[.12em] uppercase text-nav-faint m-0 mb-1.5 inline-flex items-center gap-1.5">
              <Lightbulb className="h-3.5 w-3.5 shrink-0" aria-hidden />
              {hooksLabel}
            </p>
            <ul className="list-disc ml-4 mt-1 space-y-0.5 text-[13px] leading-[1.65] text-nav-slate">
              {hooks.map((h, i) => (
                <li key={i}>{h}</li>
              ))}
            </ul>
          </div>
        )}
        {note && (
          <p
            data-testid={`domain-note-${domain}`}
            className="mt-3 text-[13px] italic leading-[1.6] text-nav-faint m-0"
          >
            {note}
          </p>
        )}
      </header>

      <div aria-labelledby={headingId} className="space-y-5">
        <DomainBody {...props} />
      </div>

      {/* S2 — el alcance de la caja va en la ETIQUETA, no sólo en el
          placeholder: el placeholder desaparece al primer carácter tipeado y
          la etiqueta queda. El aviso de abajo es blando por decisión: es PHI
          dictada en una llamada, y bloquear un guardado pierde información
          clínica que no se recupera. */}
      <div className="space-y-1.5 pt-2 border-t border-nav-hair">
        <Label htmlFor={`notes-${domain}`}>
          {tr('Notes for this domain (optional)', 'Notas de este dominio (opcional)')}
        </Label>
        <Textarea
          id={`notes-${domain}`}
          value={props.notes}
          onChange={(e) => props.onNotesChange(e.target.value)}
          placeholder={notesPlaceholderFor(domain, lang)}
          maxLength={NOTES_MAX_LENGTH}
        />
        <div className="flex items-start justify-between gap-3">
          {shouldShowNotesHint(props.notes) ? (
            <p
              data-testid={`notes-hint-${domain}`}
              className="text-[11px] text-nav-slate italic m-0 flex items-start gap-1.5"
            >
              <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" aria-hidden />
              {tr(
                `Does this answer the "${title}" question? If it is about something else, move it to its own domain.`,
                `¿Esto responde a la pregunta de ${title.toLowerCase()}? Si es de otro tema, movelo a su dominio.`,
              )}
            </p>
          ) : (
            <span />
          )}
          <p
            data-testid={`notes-counter-${domain}`}
            className="text-[11px] text-nav-faint m-0 tabular-nums shrink-0"
          >
            {props.notes.length}/{NOTES_MAX_LENGTH}
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-domain bodies
// ---------------------------------------------------------------------------

function DomainBody({ domain, answer, onChange }: DomainFormProps): JSX.Element {
  const lang = useLanguageStore((store) => store.lang);
  const tr = (en: string, es: string): string => (lang === 'es' ? es : en);
  switch (domain) {
    case 'living_situation':
      return (
        <RadioGroup
          name="living_situation"
          value={(answer.answer as string) ?? null}
          onChange={(v) => onChange({ answer: v })}
          options={[
            { value: 'steady', label: tr('I have a steady place to live', 'Tengo un lugar estable para vivir') },
            {
              value: 'worried',
              label: 'I have a place to live today',
              description: tr('But I am worried about losing it in the future', 'Pero me preocupa perderlo en el futuro'),
            },
            {
              value: 'unstable',
              label: tr('I do not have a steady place to live', 'No tengo un lugar estable para vivir'),
              description: tr('Hotel, shelter, car, with others, on the street, etc.', 'Hotel, refugio, coche, con otros, en la calle, etc.'),
            },
          ]}
        />
      );

    case 'housing_conditions': {
      const sel = (answer.selections as string[]) ?? [];
      const toggle = (val: string): void => {
        if (val === 'none') {
          onChange({ selections: sel.includes('none') ? [] : ['none'] });
          return;
        }
        const without = sel.filter((s) => s !== 'none' && s !== val);
        if (sel.includes(val)) {
          onChange({ selections: without });
        } else {
          onChange({ selections: [...without, val] });
        }
      };
      return (
        <div className="space-y-2.5">
          <p className="text-xs text-nav-slate">
            {tr('Select all that apply. Choosing "None" clears the others.',
                'Marcá todas las que apliquen. Elegir «Ninguna» limpia las demás.')}
          </p>
          {HOUSING_CONDITION_OPTIONS.map((opt) => (
            <Checkbox
              key={opt.value}
              checked={sel.includes(opt.value)}
              onChange={() => toggle(opt.value)}
              label={lang === 'es' ? opt.labelEs : opt.label}
            />
          ))}
        </div>
      );
    }

    case 'utilities':
      return (
        <RadioGroup
          name="utilities"
          value={(answer.answer as string) ?? null}
          onChange={(v) => onChange({ answer: v })}
          options={[
            { value: 'yes', label: tr('Yes, threatened in the past 12 months', 'Sí, amenazado en los últimos 12 meses') },
            { value: 'no', label: tr('No', 'No') },
            { value: 'already_shut_off', label: tr('Already shut off', 'Ya cortado') },
          ]}
        />
      );

    case 'food':
      return (
        <div className="space-y-5">
          <div className="space-y-1.5">
            <p className="text-sm text-nav-navy font-semibold font-body">
              Worried food would run out before money came (last 12 mo)
            </p>
            <RadioGroup
              name="worried_running_out"
              value={(answer.worried_running_out as string) ?? null}
              onChange={(v) => onChange({ ...answer, worried_running_out: v })}
              options={FOOD_FREQUENCY_OPTIONS.map((o) => ({ value: o.value, label: lang === 'es' ? o.labelEs : o.label }))}
              inline
            />
          </div>
          <div className="space-y-1.5">
            <p className="text-sm text-nav-navy font-semibold font-body">
              Food bought didn't last and you couldn't get more (last 12 mo)
            </p>
            <RadioGroup
              name="food_didnt_last"
              value={(answer.food_didnt_last as string) ?? null}
              onChange={(v) => onChange({ ...answer, food_didnt_last: v })}
              options={FOOD_FREQUENCY_OPTIONS.map((o) => ({ value: o.value, label: lang === 'es' ? o.labelEs : o.label }))}
              inline
            />
          </div>
        </div>
      );

    case 'transportation':
      return (
        <RadioGroup
          name="transportation"
          value={(answer.answer as string) ?? null}
          onChange={(v) => onChange({ answer: v })}
          options={[
            { value: 'yes', label: tr('Yes', 'Sí') },
            { value: 'no', label: tr('No', 'No') },
          ]}
          inline
        />
      );

    case 'career':
      return (
        <RadioGroup
          name="career"
          value={(answer.answer as string) ?? null}
          onChange={(v) => onChange({ answer: v })}
          options={[
            { value: 'help_finding', label: tr('Yes — help finding work', 'Sí — ayuda para encontrar trabajo') },
            { value: 'help_keeping', label: tr('Yes — help keeping work', 'Sí — ayuda para mantener el trabajo') },
            { value: 'no_help', label: tr('I do not need or want help', 'No necesito ni quiero ayuda') },
          ]}
        />
      );

    case 'school':
      return (
        <RadioGroup
          name="school"
          value={(answer.answer as string) ?? null}
          onChange={(v) => onChange({ answer: v })}
          options={[
            { value: 'yes', label: tr('Yes', 'Sí') },
            { value: 'no', label: tr('No', 'No') },
          ]}
          inline
        />
      );

    case 'safety': {
      const fields: Array<{ key: 'physical_hurt' | 'insult' | 'threaten' | 'scream_curse'; label: string }> = [
        { key: 'physical_hurt', label: tr('How often does anyone physically hurt you?', '¿Con qué frecuencia alguien le lastima físicamente?') },
        { key: 'insult', label: tr('How often does anyone insult or talk down to you?', '¿Con qué frecuencia alguien le insulta o le habla con desprecio?') },
        { key: 'threaten', label: tr('How often does anyone threaten you with harm?', '¿Con qué frecuencia alguien le amenaza con hacerle daño?') },
        { key: 'scream_curse', label: tr('How often does anyone scream or curse at you?', '¿Con qué frecuencia alguien le grita o le insulta?') },
      ];
      const total = computeSafetyTotal(answer);
      const handleChange = (key: string, value: string): void => {
        const next = { ...answer, [key]: value };
        next.total_score = computeSafetyTotal(next);
        onChange(next);
      };
      // HIPAA hotfix 2026-05-13 — A-12: live banner fires on per-question
      // high-risk (physical_hurt / threaten in fairly_often or frequently)
      // even when total < 11. Mirrors backend computePositiveFinding('safety').
      const isHighRisk = (v: unknown): boolean =>
        v === 'fairly_often_4' || v === 'frequently_5';
      const highRiskPhysical = isHighRisk(answer.physical_hurt);
      const highRiskThreaten = isHighRisk(answer.threaten);
      const triggerPerQuestion = highRiskPhysical || highRiskThreaten;
      const triggerTotal = total >= 11;
      const concern = triggerPerQuestion || triggerTotal;

      return (
        <div className="space-y-5">
          {fields.map((f) => (
            <div key={f.key} className="space-y-1.5">
              <p className="text-sm text-nav-navy font-semibold font-body">{f.label}</p>
              <RadioGroup
                name={f.key}
                value={(answer[f.key] as string) ?? null}
                onChange={(v) => handleChange(f.key, v)}
                options={SAFETY_LEVEL_OPTIONS.map((o) => ({ value: o.value, label: lang === 'es' ? o.labelEs : o.label }))}
              />
            </div>
          ))}
          <div
            className={cn(
              'rounded-[10px] border-[1.5px] p-3 flex items-center justify-between',
              concern
                ? 'bg-nav-redTint border-nav-red text-nav-red'
                : 'bg-nav-soft border-nav-card text-nav-slate',
            )}
            role="status"
          >
            <div className="flex items-center gap-2">
              {concern && <AlertTriangle className="h-4 w-4" />}
              <span className="text-sm font-medium">
                {concern ? tr('Safety concern flagged', 'Alerta de seguridad marcada') : tr('Total score', 'Puntaje total')}
              </span>
            </div>
            <span className="text-2xl font-semibold tabular-nums">{total}</span>
          </div>
          {concern && (
            <div className="text-xs text-nav-red space-y-1">
              <p className="font-medium">
                {tr('Supervisor is being notified now. Continue the screening and consider including DV / safety resources.',
                    'El supervisor está siendo notificado. Continuá el screening e incluí recursos de DV / seguridad.')}
              </p>
              <p>
                {triggerPerQuestion && triggerTotal
                  ? tr('Triggered by: high-risk response + total score 11+.',
                       'Disparado por: respuesta de alto riesgo + score total 11+.')
                  : triggerPerQuestion
                  ? tr(`Triggered by high-risk response: ${highRiskPhysical ? 'physical_hurt' : ''}${highRiskPhysical && highRiskThreaten ? ' + ' : ''}${highRiskThreaten ? 'threaten' : ''}.`,
                       `Disparado por respuesta de alto riesgo: ${highRiskPhysical ? 'physical_hurt' : ''}${highRiskPhysical && highRiskThreaten ? ' + ' : ''}${highRiskThreaten ? 'threaten' : ''}.`)
                  : tr('Triggered by total score 11+.', 'Disparado por score total 11+.')}
              </p>
            </div>
          )}
        </div>
      );
    }

    case 'physical_ability':
      return (
        <div className="space-y-5">
          <div className="space-y-1.5">
            <p className="text-sm text-nav-navy font-semibold font-body">
              Serious difficulty walking or climbing stairs?
            </p>
            <RadioGroup
              name="walking_stairs"
              value={(answer.walking_stairs as string) ?? null}
              onChange={(v) => onChange({ ...answer, walking_stairs: v })}
              options={PHYSICAL_ABILITY_OPTIONS.map((o) => ({ value: o.value, label: lang === 'es' ? o.labelEs : o.label }))}
              inline
            />
          </div>
          <div className="space-y-1.5">
            <p className="text-sm text-nav-navy font-semibold font-body">Difficulty dressing or bathing?</p>
            <RadioGroup
              name="dressing_bathing"
              value={(answer.dressing_bathing as string) ?? null}
              onChange={(v) => onChange({ ...answer, dressing_bathing: v })}
              options={PHYSICAL_ABILITY_OPTIONS.map((o) => ({ value: o.value, label: lang === 'es' ? o.labelEs : o.label }))}
              inline
            />
          </div>
        </div>
      );
  }
}
